import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	forwardRef,
	inject,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from "@angular/core";
import {
	ControlValueAccessor,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	UntypedFormControl,
	UntypedFormGroup,
} from "@angular/forms";
import { Subject } from "rxjs";
import { filter, takeUntil, tap } from "rxjs/operators";

import { TimeValidator } from "@intocare/validators";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => TimeInputComponent),
	multi: true,
};

const TIME_VALIDATOR = {
	provide: NG_VALIDATORS,
	useValue: TimeValidator,
	multi: true,
};

@Component({
	selector: "ic-time-input",
	styleUrls: ["input.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR, TIME_VALIDATOR],
	templateUrl: "./input.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TimeInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
	private cdRef = inject(ChangeDetectorRef);

	private minutes: string;
	private hour: string;
	private destroy$ = new Subject<void>();
	form = new UntypedFormGroup({
		hour: new UntypedFormControl(""),
		minutes: new UntypedFormControl(""),
	});
	@Input() label: string;
	@Input() placeholder = "";
	@Input() icon = "fa fa-clock-o";
	@Input() required = true;
	@Input() help: string;
	@Input()
	set fixedMinutes(minutes: string) {
		const control = this.form.get("minutes");
		this.minutes = minutes;

		if (!minutes) {
			control.enable();

			return;
		}

		this.form.patchValue({ minutes });
		control.disable({ emitEvent: false });
	}

	@Input()
	set fixeHour(hour: string) {
		const control = this.form.get("hour");
		this.hour = hour;

		if (!hour) {
			control.enable();

			return;
		}

		this.form.patchValue({ hour });
		control.disable({ emitEvent: false });
	}
	// Minutes input field
	@ViewChild("minutesInput", { static: false }) minutesInput: ElementRef;

	value: any = "";
	disabled = false;

	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onModelChange = (_: unknown) => {};

	writeValue(value: string) {
		if (!value) {
			this.form.reset(
				{
					hour: "",
					minutes: "",
				},
				{ emitEvent: false },
			);

			if (this.minutes) {
				this.form.patchValue({ minutes: this.minutes }, { emitEvent: false });
			}

			if (this.hour) {
				this.form.patchValue({ hour: this.hour }, { emitEvent: false });
			}

			this.cdRef.markForCheck();

			return;
		}

		if (typeof value !== "string" || value.split(":").length !== 2) {
			return;
		}

		const [hour, minutes] = value.split(":");
		this.form.patchValue({ hour, minutes }, { emitEvent: false });

		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;

		if (isDisabled) {
			this.form.disable({ emitEvent: false });
		} else {
			this.form.enable({ emitEvent: false });
		}

		this.cdRef.markForCheck();
	}

	onTouched() {
		if ((!this.form.get("hour").touched && !this.hour) || (!this.form.get("minutes").touched && !this.minutes)) {
			return;
		}

		this.onTouch();
	}

	onTab(controlName: "hour" | "minutes") {
		const control = this.form.get(controlName);

		if (control.value) {
			return;
		}

		control.setValue("00");
	}

	// Add a leading zero to the input
	onBlur(controlName: "hour" | "minutes") {
		this.onTouched();

		const isHour = controlName === "hour";
		const control = this.form.get(controlName);
		const value = Number(control.value);

		// If the value is not a correct hour or mintues, reset
		if (control.value === "" || Number.isNaN(value) || value < 0 || (isHour && value > 23) || (!isHour && value > 59)) {
			control.reset("");

			return;
		}

		// If the value is higher than 9, we have two digits
		if (value > 9) {
			return;
		}

		control.setValue(`0${value}`);
	}

	/** Automatically focus on the minutes input in case the hour input is valid. */
	private setupMinutesInputFocusListener() {
		this.form
			.get("hour")
			.valueChanges.pipe(
				filter((value) => !(Number.isNaN(value) || Number(value) < 0 || Number(value) > 23 || value.length !== 2)),
				tap(() => {
					this.minutesInput.nativeElement.focus();
				}),
				takeUntil(this.destroy$),
			)
			.subscribe();
	}

	ngOnInit() {
		this.form.valueChanges
			.pipe(
				tap(() => {
					const { hour, minutes } = this.form.getRawValue();
					if (!hour && !minutes) {
						this.onModelChange(null);
					} else {
						this.onModelChange(`${hour}:${minutes}`);
					}
				}),
				takeUntil(this.destroy$),
			)
			.subscribe();
		this.setupMinutesInputFocusListener();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
